import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import './score-sheet.css';

/**
 * Le cinque forme della fila.
 *
 * `winPlus` nasce in arena — una vittoria che è valsa più di due punti, per
 * serie o boost — e nei tornei non esiste, che non hanno né l'una né l'altro.
 * `bye` nasce nei tornei a turni e in arena non esiste, che non ha turni.
 */
export type EsitoPedina = 'win' | 'winPlus' | 'draw' | 'loss' | 'bye';

/** Pedina (12px) più il gap (4px): il passo con cui cresce la fila. */
const PASSO_PEDINA = 16;

/**
 * Quante pedine stanno su una riga dedicata a 360px, misurate: la riga dei
 * numeri è larga ~258px, cioè sedici passi. Serve solo a decidere se la
 * legenda sopra la lista deve parlare del taglio: una frase che dice «le più
 * vecchie restano fuori» sopra una classifica in cui niente è tagliato è una
 * bugia, e la sfumatura invece è per riga e sa regolarsi da sé.
 *
 * È anche la pietra tombale del vecchio tetto: fino al 19 ottobre 2026 la fila
 * si fermava alle ultime otto, che era la capienza misurata quando divideva la
 * riga con la pastiglia di stato.
 */
export const CAPIENZA_A_360 = 16;

interface ScoreSheetProps {
  /** Ordine cronologico, la più recente ULTIMA: si taglia a sinistra. */
  esiti: EsitoPedina[];
  /** Il testid della fila, che le due classifiche hanno diverso. */
  testId: string;
  /**
   * La classe di COLLOCAZIONE del chiamante: dove sta la fila nella riga, non
   * come è fatta. Va per prima nell'attributo, per chi legge il DOM.
   */
  className?: string;
}

/**
 * La fila delle partite giocate, una pedina per partita.
 *
 * La porta la classifica dell'arena e quella dei tornei a turni: le forme sono
 * le stesse, e il taglio — tutte le pedine, la più vecchia fuori quando non
 * ci stanno — è un meccanismo solo. Vedi `score-sheet.css` per il come.
 */
export function ScoreSheet({ esiti, testId, className }: ScoreSheetProps) {
  const { t } = useTranslation();
  const nodo = useRef<HTMLSpanElement>(null);

  /*
   * Il misuratore: dice al foglio se la fila ci sta tutta.
   *
   * Non si può chiedere al CSS — `justify-content` non ha un «a sinistra
   * finché ci sta» — e non si può chiedere allo `scrollWidth`, che su questo
   * taglio è cieco (il perché sta nel foglio). Si confronta il BISOGNO
   * calcolato con la larghezza che l'elemento ha davvero.
   *
   * Si scrive sul nodo e non in uno stato: l'attributo cambia l'allineamento e
   * la maschera, non la misura, quindi non c'è re-render e non si chiude
   * nessun anello con l'osservatore. Venti righe di classifica che si
   * ridisegnano ogni due secondi sono il motivo.
   */
  useEffect(() => {
    const el = nodo.current;
    if (!el) return;

    const serve = esiti.length * PASSO_PEDINA - 4;
    const misura = () => {
      /* Mezzo pixel di tolleranza: le larghezze arrivano frazionarie. */
      if (serve <= el.clientWidth + 0.5) el.dataset.staTutta = '';
      else delete el.dataset.staTutta;
    };

    misura();

    // jsdom non ha il ResizeObserver: la misura sincrona qui sopra e' comunque
    // gia' andata. Stessa guardia di useAltezzaPubblicata.ts.
    if (typeof ResizeObserver === 'undefined') return;

    /* Si osserva l'elemento e non la finestra: la riga si stringe anche per
       cose che la finestra non vede — una pastiglia di stato che cambia
       parola, la colonna laterale della partita che si riapre. */
    const osservatore = new ResizeObserver(misura);
    osservatore.observe(el);
    return () => osservatore.disconnect();
  }, [esiti.length]);

  return (
    <span
      ref={nodo}
      className={[className, 'score-sheet'].filter(Boolean).join(' ')}
      data-testid={testId}
      role="img"
      /*
       * L'elenco INTERO, anche quando è lungo: la fila è una sola fermata per
       * chi ascolta, non sessanta, e l'ordine è tutto il suo contenuto —
       * troncare l'etichetta renderebbe incompleta l'unica resa integrale del
       * dato. Le pedine sono mute per definizione (`aria-hidden`), quindi
       * senza questa etichetta la fila sparirebbe del tutto.
       */
      aria-label={t('tournament.standings.scoreSheet.label', {
        esiti: esiti.map((e) => t(`tournament.standings.scoreSheet.outcome.${e}`)).join(', '),
      })}
    >
      {esiti.map((esito, i) => (
        /* `key={i}`: l'array cresce solo in coda, quindi l'indice è stabile. */
        <i key={i} className="score-disc" data-kind={esito} aria-hidden="true" />
      ))}
    </span>
  );
}
